import { useState } from "react";
import { toast } from "sonner";
import { Loader2, MailPlus, Check } from "lucide-react";
import { readableError } from "@/lib/errors";

// Same-origin `/api` — Emergent's ingress routes `/api/*` to the backend on
// every frontend host (preview, .emergent.host, custom domain), so no
// `REACT_APP_BACKEND_URL` swap is needed and CORS never applies.
const API = "/api";

/**
 * Inline consumer waitlist capture used inside the Consumers ("For You")
 * section. Only email is required; city is a helpful signal for the launch
 * team. Success state is persisted to localStorage so returning visitors
 * see the "You're on the list" confirmation instead of the form.
 */
export default function ConsumerWaitlistForm() {
  const [form, setForm] = useState({ email: "", city: "" });
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(() => {
    try {
      return localStorage.getItem("bookaway_consumer_subscribed") === "1";
    } catch {
      return false;
    }
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/consumer-waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(readableError(body, "Something went wrong."));
      }
      try {
        localStorage.setItem("bookaway_consumer_subscribed", "1");
      } catch {}
      setSubscribed(true);
      toast.success("You're on the launch list.");
    } catch (err) {
      toast.error(err.message || "Could not save your details.");
    } finally {
      setSubmitting(false);
    }
  };

  if (subscribed) {
    return (
      <div
        data-testid="consumer-waitlist-success"
        className="mt-6 flex items-start gap-3 rounded-2xl border border-gold/40 bg-gold/10 p-5 text-[#2A2724]"
      >
        <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold text-black">
          <Check className="h-4 w-4" strokeWidth={2.5} />
        </span>
        <div>
          <p className="font-serif text-lg leading-tight">
            You're on the launch list.
          </p>
          <p className="mt-1 text-sm text-[#6B6B6B]">
            We'll ping you the moment Book Away opens in your city.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="consumer-waitlist-form"
      className="mt-6 rounded-2xl border border-black/10 bg-white/60 p-5 backdrop-blur-sm"
    >
      <div className="mb-3 flex items-center gap-2 text-[#2A2724]">
        <MailPlus className="h-4 w-4 text-gold" />
        <p className="text-xs font-medium uppercase tracking-[0.22em] text-[#8B6F1F]">
          Not in your city yet?
        </p>
      </div>
      <p className="mb-4 font-serif text-xl leading-snug text-[#2A2724]">
        Get pinged the moment we launch near you.
      </p>
      <div className="flex flex-col gap-3 sm:flex-row">
        <input
          data-testid="consumer-waitlist-email"
          required
          type="email"
          value={form.email}
          onChange={set("email")}
          placeholder="you@example.com"
          className="flex-[2] rounded-full border border-black/15 bg-white px-5 py-3 text-sm text-[#2A2724] placeholder:text-[#9a9a9a] outline-none focus:border-gold"
        />
        <input
          data-testid="consumer-waitlist-city"
          type="text"
          value={form.city}
          onChange={set("city")}
          placeholder="City or postcode"
          className="flex-1 rounded-full border border-black/15 bg-white px-5 py-3 text-sm text-[#2A2724] placeholder:text-[#9a9a9a] outline-none focus:border-gold"
        />
        <button
          type="submit"
          data-testid="consumer-waitlist-submit"
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2A2724] px-6 py-3 text-sm font-medium text-white transition-colors duration-300 hover:bg-black disabled:opacity-70"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Adding…
            </>
          ) : (
            "Notify me"
          )}
        </button>
      </div>
      <p className="mt-3 text-[0.7rem] uppercase tracking-[0.22em] text-[#9a9a9a]">
        One email at launch. No spam, ever.
      </p>
    </form>
  );
}
